import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Avatar, Button, Card, Col, Divider, Empty, Input, InputNumber, List, Row,
    Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    BankOutlined, SearchOutlined, PlusOutlined, DeleteOutlined, SaveOutlined,
    CheckCircleFilled, WarningFilled, ArrowLeftOutlined, FileTextOutlined,
    PercentageOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;

const PRIMARY = "#242166";
const SUCCESS = "#16a34a";
const DANGER = "#dc2626";

const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;
const dossierNum = (d) => d?.numSinistre || d?.numeroSinistreM || d?.numeroSinistreP || "—";
// N° de sinistre de la COMPAGNIE (ancien système) : affiché sous le n° PoolIns pour que les
// utilisateurs encore sur leur ancien référentiel s'y retrouvent. Masqué s'il est absent ou
// identique au n° déjà affiché.
const dossierCie = (d) => {
    const m = (d?.numeroSinistreM || "").trim();
    return m && m !== dossierNum(d) ? m : "";
};
const milThousand = (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const milParse = (v) => `${v}`.replace(/\s/g, "");

/**
 * Règlement multiple des conseils juridiques — v2 (AntD v5). Même modèle que les
 * experts : honoraires + frais accessoires + retenue (3/5/20 %). Endpoints :
 * GET /api/conseil-juridique, GET /api/sinistre/conseil-juridique/{id},
 * POST /api/decaissement/all-conseils-juridiques.
 */
const GestionPaiementSinistreConseilsJuridiquesV2 = () => {
    const navigate = useNavigate();

    const [conseils, setConseils] = useState([]);
    const [conseil, setConseil] = useState(null);
    // Taux PARAMÉTRABLES, appliqués UNIQUEMENT aux honoraires (défaut retenue 5 %, TVA 18 %).
    const [tauxRetenue, setTauxRetenue] = useState(5);
    const [tauxTva, setTauxTva] = useState(18);

    const [sinistres, setSinistres] = useState([]);
    const [loadingDossiers, setLoadingDossiers] = useState(false);
    const [searchDossier, setSearchDossier] = useState("");

    const [selection, setSelection] = useState([]);
    const [saving, setSaving] = useState(false);
    // Reste disponible par dossier = SAP − paiements en attente (validation ou chèque) :
    // le contrôle d'écran grise la ligne dès que la saisie dépasse (garde serveur en plus).
    const [restesSap, setRestesSap] = useState({});

    // ── Chargement des conseils juridiques ────────────────────────────────────
    useEffect(() => {
        axiosInstance.get("/api/conseil-juridique")
            .then((res) => setConseils(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des conseils juridiques."); });
    }, []);

    // ── Sélection d'un conseil → chargement de ses dossiers ───────────────────
    const handleSelectConseil = (conseilJuridiqueId) => {
        const c = conseils.find((x) => x.conseilJuridiqueId === conseilJuridiqueId) || null;
        setConseil(c);
        setSelection([]);
        setSearchDossier("");
        setTauxRetenue(5);
        setTauxTva(18);
        if (!c) { setSinistres([]); return; }

        setLoadingDossiers(true);
        axiosInstance.get(`/api/sinistre/conseil-juridique/${c.conseilJuridiqueId}`)
            .then((res) => {
                const data = Array.isArray(res.data)
                    ? res.data
                    : res.data?.content ?? res.data?.data ?? res.data?.items ?? [];
                setSinistres(data);
                const codeIds = data.map((d) => d.codeId).filter(Boolean);
                if (codeIds.length) {
                    axiosInstance.post("/api/decaissement/reste-sap", JSON.stringify(codeIds),
                        { headers: { "Content-Type": "application/json" } })
                        .then((r) => {
                            const map = {};
                            (r.data || []).forEach((x) => { map[x.sinistreCodeId] = Number(x.resteDisponible); });
                            setRestesSap(map);
                        })
                        .catch(() => setRestesSap({}));
                }
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des dossiers."); })
            .finally(() => setLoadingDossiers(false));
    };

    // ── Panier ────────────────────────────────────────────────────────────────
    // Somme des postes saisis d'un dossier (honoraires + 4 frais).
    const sommePostes = (d) => Number(d.honoraires || 0) + Number(d.fraisGestion || 0)
        + Number(d.fraisPvConstat || 0) + Number(d.fraisVignette || 0) + Number(d.fraisDeplacement || 0);

    const ajouter = (dossier) => {
        if (selection.find((d) => d.codeId === dossier.codeId)) return;
        setSelection((prev) => [...prev, {
            ...dossier, honoraires: 0,
            fraisGestion: 0, fraisPvConstat: 0, fraisVignette: 0, fraisDeplacement: 0,
            gestionPaye: false, pvPaye: false,
        }]);
        // Frais de gestion / PV de constat déjà réglés pour ce dossier → on grisera la saisie.
        if (conseil) {
            axiosInstance
                .get(`/api/decaissement/conseils-juridiques/postes-payes/${dossier.codeId}/${conseil.conseilJuridiqueId}`)
                .then((res) => {
                    const postes = Array.isArray(res.data) ? res.data : [];
                    setSelection((prev) => prev.map((d) => d.codeId === dossier.codeId
                        ? { ...d, gestionPaye: postes.includes("FRAIS_GESTION"), pvPaye: postes.includes("FRAIS_PV_CONSTAT") }
                        : d));
                })
                .catch(() => { /* indisponible → pas de grisage, la garde backend reste */ });
            // Statut des frais de DÉPLACEMENT (conditions contractuelles du conseil :
            // plafond de paiements par dossier, juridictions exclues, plafond individuel).
            axiosInstance
                .post(`/api/decaissement/conseils-juridiques/deplacements-status/${conseil.conseilJuridiqueId}`,
                    JSON.stringify([dossier.codeId]), { headers: { "Content-Type": "application/json" } })
                .then((res) => {
                    const st = Array.isArray(res.data) ? res.data[0] : null;
                    if (st) {
                        setSelection((prev) => prev.map((d) => d.codeId === dossier.codeId
                            ? { ...d, deplacementStatus: st } : d));
                    }
                })
                .catch(() => { /* indisponible → la garde backend reste */ });
        }
    };
    const retirer = (codeId) => setSelection((prev) => prev.filter((d) => d.codeId !== codeId));
    const setMontant = (codeId, champ, valeur) =>
        setSelection((prev) => prev.map((d) => d.codeId === codeId ? { ...d, [champ]: Number(valeur) || 0 } : d));

    const resteDossier = (d) => (restesSap[d.codeId] != null ? restesSap[d.codeId] : Number(d.sap ?? Infinity));
    const estValide = (d) => sommePostes(d) <= resteDossier(d) + 0.01;

    const dossiersDisponibles = useMemo(() => {
        const q = searchDossier.trim().toLowerCase();
        return sinistres.filter((d) =>
            !selection.find((s) => s.codeId === d.codeId) &&
            (!q || dossierNum(d).toLowerCase().includes(q) || dossierCie(d).toLowerCase().includes(q))
        );
    }, [sinistres, selection, searchDossier]);

    const totaux = useMemo(() => {
        const totalHonoraires = selection.reduce((a, d) => a + Number(d.honoraires || 0), 0);
        const totalFrais = selection.reduce((a, d) => a + Number(d.fraisGestion || 0)
            + Number(d.fraisPvConstat || 0) + Number(d.fraisVignette || 0) + Number(d.fraisDeplacement || 0), 0);
        // TVA et retenue UNIQUEMENT sur les honoraires ; frais à valeur faciale.
        const montantTva = totalHonoraires * (tauxTva / 100);
        const montantRetenue = totalHonoraires * (tauxRetenue / 100);
        const totalNet = totalHonoraires + montantTva - montantRetenue + totalFrais;
        return { totalHonoraires, totalFrais, montantTva, montantRetenue, totalNet };
    }, [selection, tauxRetenue, tauxTva]);

    const erreurs = selection.filter((d) => !estValide(d)).length;
    const peutEnregistrer = selection.length > 0 && erreurs === 0
        && (totaux.totalHonoraires + totaux.totalFrais) > 0;

    const enregistrer = () => {
        if (!peutEnregistrer || !conseil) return;
        setSaving(true);
        const payload = selection.map((d) => ({
            codeId: d.codeId,
            honoraires: Number(d.honoraires || 0),
            fraisGestion: Number(d.fraisGestion || 0),
            fraisPvConstat: Number(d.fraisPvConstat || 0),
            fraisVignette: Number(d.fraisVignette || 0),
            fraisDeplacement: Number(d.fraisDeplacement || 0),
            retenue: tauxRetenue,
            tva: tauxTva,
            conseilJuridiqueId: conseil.conseilJuridiqueId,
        }));
        axiosInstance.post("/api/decaissement/all-conseils-juridiques", JSON.stringify(payload), {
            headers: { "Content-Type": "application/json" },
        })
            .then(() => {
                toast.success("Règlement enregistré avec succès.");
                setSelection([]);
                setConseil(null);
                setSinistres([]);
            })
            .catch((e) => {
                console.error(e);
                // Rejet backend (ex. frais de gestion/PV déjà réglés) → on remonte le message.
                const msg = e?.response?.data?.message || e?.response?.data?.error;
                toast.error(msg || "Erreur lors de l'enregistrement du règlement.");
            })
            .finally(() => setSaving(false));
    };

    const conseilOptions = useMemo(() => conseils.map((c) => ({
        value: c.conseilJuridiqueId,
        label: c.nom,
        adresse: c.adresse,
        tel: c.telephone1,
    })), [conseils]);

    // ── Colonnes du tableau « Dossiers à régler » ─────────────────────────────
    // Colonne de frais (valeur faciale). `payeFlag` (gestion/PV) → grise la saisie si déjà réglé.
    const fraisCol = (title, champ, payeFlag) => ({
        title, key: champ, width: 140,
        render: (_, d) => {
            const paye = payeFlag ? !!d[payeFlag] : false;
            return (
                <Tooltip title={paye ? "Déjà réglé pour ce dossier (payable une seule fois)" : ""}>
                    <InputNumber
                        style={{ width: "100%" }} min={0} value={paye ? 0 : (d[champ] || 0)} disabled={paye}
                        controls={false} formatter={milThousand} parser={milParse}
                        placeholder={paye ? "Déjà réglé" : ""}
                        onChange={(v) => setMontant(d.codeId, champ, v)}
                    />
                </Tooltip>
            );
        },
    });

    const columns = [
        {
            title: "Dossier", key: "dossier", width: 190,
            render: (_, d) => (
                <div>
                    <Text strong style={{ fontFamily: "monospace" }}>{dossierNum(d)}</Text>
                    {dossierCie(d) && (
                        <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
                            N°&nbsp;cie&nbsp;: {dossierCie(d)}
                        </div>
                    )}
                    {restesSap[d.codeId] != null
                        ? <div style={{ fontSize: 12, color: "#9ca3af" }}>Disponible&nbsp;: {fcfa(restesSap[d.codeId])}</div>
                        : (d.sap != null && <div style={{ fontSize: 12, color: "#9ca3af" }}>SAP&nbsp;: {fcfa(d.sap)}</div>)}
                </div>
            ),
        },
        {
            title: "Honoraires", key: "honoraires", width: 150,
            render: (_, d) => (
                <InputNumber
                    style={{ width: "100%" }} min={0} value={d.honoraires || 0}
                    controls={false} formatter={milThousand} parser={milParse}
                    onChange={(v) => setMontant(d.codeId, "honoraires", v)}
                />
            ),
        },
        fraisCol("Frais de gestion", "fraisGestion", "gestionPaye"),
        fraisCol("Frais PV de constat", "fraisPvConstat", "pvPaye"),
        fraisCol("Frais de vignettes", "fraisVignette"),
        {
            // Déplacements : soumis aux conditions contractuelles du conseil (plafond de
            // paiements par dossier / juridictions exclues) et au plafond individuel du dossier.
            title: "Frais de déplacement", key: "fraisDeplacement", width: 150,
            render: (_, d) => {
                const st = d.deplacementStatus;
                const bloque = !!st?.bloque;
                return (
                    <Tooltip title={bloque
                        ? st.motifBlocage
                        : (st?.plafondEffectif != null
                            ? `Déplacements payés : ${st.deplacementsPayes}/${st.plafondEffectif}`
                            : "")}>
                        <div>
                            <InputNumber
                                style={{ width: "100%" }} min={0}
                                value={bloque ? 0 : (d.fraisDeplacement || 0)} disabled={bloque}
                                controls={false} formatter={milThousand} parser={milParse}
                                placeholder={bloque ? "Non pris en charge" : ""}
                                onChange={(v) => setMontant(d.codeId, "fraisDeplacement", v)}
                            />
                            {st?.plafondEffectif != null && !bloque && (
                                <div style={{ fontSize: 11, color: "#9ca3af" }}>
                                    {st.deplacementsPayes}/{st.plafondEffectif} payé(s)
                                </div>
                            )}
                            {bloque && (
                                <div style={{ fontSize: 11, color: DANGER }}>
                                    {st?.juridictionExclue ? "Juridiction non prise en charge" : "Plafond atteint"}
                                </div>
                            )}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: "Total", key: "total", width: 150, align: "right",
            render: (_, d) => {
                const total = sommePostes(d);
                const ok = estValide(d);
                return (
                    <div>
                        <Text strong style={{ color: ok ? PRIMARY : DANGER }}>{fcfa(total)}</Text>
                        <div>
                            {ok
                                ? <Tag color="success" icon={<CheckCircleFilled />} style={{ marginInlineEnd: 0 }}>OK</Tag>
                                : <Tag color="error" icon={<WarningFilled />} style={{ marginInlineEnd: 0 }}>Dépasse le disponible</Tag>}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "", key: "action", width: 44, align: "center",
            render: (_, d) => (
                <Tooltip title="Retirer">
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => retirer(d.codeId)} />
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── En-tête ── */}
                            <div style={{
                                background: `linear-gradient(120deg, ${PRIMARY} 0%, #3b378f 100%)`,
                                borderRadius: 16, padding: "22px 26px", marginBottom: 18,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                flexWrap: "wrap", gap: 12, boxShadow: "0 6px 18px rgba(36,33,102,0.22)",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                    <div style={{
                                        width: 50, height: 50, borderRadius: 12, background: "rgba(255,255,255,0.15)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <BankOutlined style={{ fontSize: 26, color: "#fff" }} />
                                    </div>
                                    <div>
                                        <Title level={3} style={{ color: "#fff", margin: 0 }}>Règlement des conseils juridiques</Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Règlement cumulé des honoraires de conseil juridique
                                        </Text>
                                    </div>
                                </div>
                                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ borderRadius: 8 }}>
                                    Retour
                                </Button>
                            </div>

                            <Row gutter={[18, 18]}>
                                {/* ── Colonne gauche : sélection ── */}
                                <Col xs={24} lg={9}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={<Space><BankOutlined style={{ color: PRIMARY }} /><span>1. Conseil juridique &amp; retenue</span></Space>}
                                    >
                                        <Text type="secondary" style={{ fontSize: 13 }}>Conseil juridique</Text>
                                        <Select
                                            showSearch allowClear style={{ width: "100%", marginTop: 6 }}
                                            placeholder="Rechercher un conseil juridique…" optionFilterProp="label"
                                            value={conseil?.conseilJuridiqueId} onChange={(v) => handleSelectConseil(v)}
                                            options={conseilOptions}
                                            optionRender={(opt) => (
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
                                                    {(opt.data.adresse || opt.data.tel) && (
                                                        <div style={{ fontSize: 12, color: "#9ca3af" }}>
                                                            {[opt.data.adresse, opt.data.tel].filter(Boolean).join(" · ")}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />

                                        {conseil && (
                                            <>
                                                <div style={{
                                                    marginTop: 14, padding: 12, borderRadius: 10, background: "#f5f5ff",
                                                    display: "flex", alignItems: "center", gap: 12,
                                                }}>
                                                    <Avatar size={42} style={{ background: PRIMARY }} icon={<BankOutlined />} />
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: PRIMARY }}>{conseil.nom}</div>
                                                        {(conseil.adresse || conseil.telephone1) && (
                                                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                                {[conseil.adresse, conseil.telephone1].filter(Boolean).join(" · ")}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <Row gutter={12} style={{ marginTop: 14 }}>
                                                    <Col span={12}>
                                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                                            <PercentageOutlined /> Retenue sur honoraires
                                                        </Text>
                                                        <InputNumber
                                                            style={{ width: "100%", marginTop: 6 }} min={0} max={100}
                                                            value={tauxRetenue} onChange={(v) => setTauxRetenue(Number(v) || 0)}
                                                            addonAfter="%"
                                                        />
                                                    </Col>
                                                    <Col span={12}>
                                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                                            <PercentageOutlined /> TVA sur honoraires
                                                        </Text>
                                                        <InputNumber
                                                            style={{ width: "100%", marginTop: 6 }} min={0} max={100}
                                                            value={tauxTva} onChange={(v) => setTauxTva(Number(v) || 0)}
                                                            addonAfter="%"
                                                        />
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </Card>

                                    {/* Dossiers disponibles */}
                                    <Card
                                        bordered={false}
                                        style={{ borderRadius: 14, marginTop: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <FileTextOutlined style={{ color: PRIMARY }} />
                                                <span>2. Dossiers disponibles</span>
                                                {conseil && <Tag color="default">{dossiersDisponibles.length}</Tag>}
                                            </Space>
                                        }
                                    >
                                        {!conseil ? (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionnez d'abord un conseil juridique" />
                                        ) : (
                                            <>
                                                <Input
                                                    allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                                    placeholder="Rechercher un dossier…" value={searchDossier}
                                                    onChange={(e) => setSearchDossier(e.target.value)} style={{ marginBottom: 12 }}
                                                />
                                                <List
                                                    loading={loadingDossiers}
                                                    dataSource={dossiersDisponibles}
                                                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={sinistres.length === 0 ? "Aucun dossier pour ce conseil juridique" : "Tous les dossiers ont été ajoutés"} /> }}
                                                    style={{ maxHeight: 360, overflowY: "auto" }}
                                                    renderItem={(d) => (
                                                        <List.Item
                                                            style={{ padding: "10px 4px" }}
                                                            actions={[
                                                                <Button key="add" type="primary" ghost size="small" icon={<PlusOutlined />} onClick={() => ajouter(d)}>
                                                                    Ajouter
                                                                </Button>,
                                                            ]}
                                                        >
                                                            <List.Item.Meta
                                                                title={
                                                                    <span style={{ fontFamily: "monospace" }}>
                                                                        {dossierNum(d)}
                                                                        {dossierCie(d) && (
                                                                            <span style={{ display: "block", fontSize: 11, color: "#9ca3af", fontWeight: 400, marginTop: 2 }}>
                                                                                N°&nbsp;cie&nbsp;: {dossierCie(d)}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                }
                                                                description={<span style={{ fontSize: 12 }}>{d.type ? `${d.type} · ` : ""}{d.sap != null ? `SAP : ${fcfa(d.sap)}` : ""}</span>}
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            </>
                                        )}
                                    </Card>
                                </Col>

                                {/* ── Colonne droite : panier + récap ── */}
                                <Col xs={24} lg={15}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <SaveOutlined style={{ color: PRIMARY }} />
                                                <span>3. Dossiers à régler</span>
                                                <Tag color={selection.length ? "processing" : "default"}>{selection.length}</Tag>
                                                {erreurs > 0 && <Tag color="error">{erreurs} en erreur</Tag>}
                                            </Space>
                                        }
                                    >
                                        <Table
                                            rowKey="codeId" size="middle" columns={columns} dataSource={selection}
                                            pagination={false} scroll={{ x: "max-content" }}
                                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ajoutez des dossiers depuis la liste de gauche" /> }}
                                        />

                                        {selection.length > 0 && (
                                            <>
                                                <Divider style={{ margin: "18px 0" }} />
                                                <Row gutter={[16, 16]} align="middle">
                                                    <Col xs={24} md={16}>
                                                        <div style={{
                                                            background: "#f8fafc", borderRadius: 12, padding: "16px 18px",
                                                            border: "1px solid #eef2f7",
                                                        }}>
                                                            <Row gutter={[16, 12]}>
                                                                <Col span={12}><Statistic title="Total honoraires" value={totaux.totalHonoraires} suffix="FCFA" valueStyle={{ fontSize: 16 }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title="Total frais" value={totaux.totalFrais} suffix="FCFA" valueStyle={{ fontSize: 16 }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title={`TVA (${tauxTva}%)`} value={totaux.montantTva} prefix="+" suffix="FCFA" valueStyle={{ fontSize: 16, color: SUCCESS }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title={`Retenue (${tauxRetenue}%)`} value={totaux.montantRetenue} prefix="-" suffix="FCFA" valueStyle={{ fontSize: 16, color: DANGER }} groupSeparator=" " /></Col>
                                                            </Row>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} md={8}>
                                                        <div style={{
                                                            background: `linear-gradient(135deg, ${SUCCESS} 0%, #15803d 100%)`,
                                                            borderRadius: 12, padding: "18px 20px", color: "#fff", textAlign: "center",
                                                        }}>
                                                            <div style={{ fontSize: 13, opacity: 0.9 }}>Montant net à payer</div>
                                                            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{fcfa(totaux.totalNet)}</div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div style={{ marginTop: 18, textAlign: "right" }}>
                                                    <Button
                                                        type="primary" size="large" icon={<SaveOutlined />}
                                                        loading={saving} disabled={!peutEnregistrer} onClick={enregistrer}
                                                        style={{ background: peutEnregistrer ? SUCCESS : undefined, border: "none", borderRadius: 10, fontWeight: 600 }}
                                                    >
                                                        Enregistrer le règlement
                                                    </Button>
                                                    {erreurs > 0 && (
                                                        <div style={{ marginTop: 8, color: DANGER, fontSize: 13 }}>
                                                            <WarningFilled /> {erreurs} dossier(s) dépassent le SAP autorisé.
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default GestionPaiementSinistreConseilsJuridiquesV2;
